import { useRouter } from 'next/router';
import * as React from 'react';

import aiAskSourceStyles from '../../AIAskSources.module.css';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import {
  isVersionFilePreviewSource,
  refToPseudoSource,
  sourceDisplayTitle,
  sourceHref,
  sourceObjectId,
} from '../../utils/aiAskHelpers';
import { openVersionFilePreview } from '../../utils/openVersionFilePreview';
import { FormattedText } from './FormattedText';

type SourcedAnswerPart =
  | { type: 'text'; key: string; content: string }
  | {
      type: 'sourceMarker';
      key: string;
      sourceNumber: number;
      sourceRecord: unknown;
    };

function resolveSourceForMarker(
  sourceNumber: number,
  sources: unknown[] | null,
  references: unknown[]
): unknown {
  const sourceIndex = sourceNumber - 1;
  if (sourceIndex >= 0 && sources && sources[sourceIndex]) {
    return sources[sourceIndex];
  }
  if (sourceIndex >= 0 && references[sourceIndex]) {
    return refToPseudoSource(references[sourceIndex]);
  }
  return null;
}

function parseSourcedAnswerParts(
  answerText: string,
  sources: unknown[] | null,
  references: unknown[]
): SourcedAnswerPart[] {
  const parts: SourcedAnswerPart[] = [];
  const sourceMarkerPattern = /\[S(\d+)\]/g;
  let lastProcessedIndex = 0;
  let sourceMarkerMatch: RegExpExecArray | null;

  while ((sourceMarkerMatch = sourceMarkerPattern.exec(answerText)) !== null) {
    if (sourceMarkerMatch.index > lastProcessedIndex) {
      parts.push({
        type: 'text',
        key: `text-${lastProcessedIndex}`,
        content: answerText.slice(lastProcessedIndex, sourceMarkerMatch.index),
      });
    }

    const sourceNumber = parseInt(sourceMarkerMatch[1], 10);
    parts.push({
      type: 'sourceMarker',
      key: `source-${sourceMarkerMatch.index}`,
      sourceNumber,
      sourceRecord: resolveSourceForMarker(
        sourceNumber,
        sources,
        references
      ),
    });
    lastProcessedIndex =
      sourceMarkerMatch.index + sourceMarkerMatch[0].length;
  }

  if (lastProcessedIndex < answerText.length) {
    parts.push({
      type: 'text',
      key: `text-${lastProcessedIndex}`,
      content: answerText.slice(lastProcessedIndex),
    });
  }

  if (!parts.length) {
    parts.push({ type: 'text', key: 'text-all', content: answerText });
  }

  return parts;
}

export const SourcedAnswerBlock: React.FC<{
  answer: string | null;
  sources: unknown[];
  references: unknown[];
  appOrigin: string;
  indexData?: AiAskIndexData;
}> = ({ answer, sources, references, appOrigin, indexData }) => {
  const router = useRouter();
  const answerText = answer == null ? '' : String(answer);
  const sourceList =
    Array.isArray(sources) && sources.length ? sources : null;
  const answerParts = parseSourcedAnswerParts(
    answerText,
    sourceList,
    references
  );
  const sourceStyles = aiAskSourceStyles;

  return (
    <div className={sourceStyles.citationAnswer}>
      {answerParts.map((part) => {
        if (part.type === 'text') {
          return <FormattedText key={part.key} text={part.content} />;
        }

        const sourceLabel = part.sourceRecord
          ? sourceDisplayTitle(part.sourceRecord, part.sourceNumber, indexData)
          : `S${part.sourceNumber}`;
        const sourceLink = part.sourceRecord
          ? sourceHref(part.sourceRecord, appOrigin)
          : '';
        const opensFilePreview = isVersionFilePreviewSource(part.sourceRecord);
        const objectId = sourceObjectId(part.sourceRecord);
        const sourceChip = (
          <span className={sourceStyles.citationChip}>
            <strong className={sourceStyles.citationChipNum}>
              S{part.sourceNumber}
            </strong>
            <span className={sourceStyles.citationChipTitle}>{sourceLabel}</span>
          </span>
        );

        if (sourceLink) {
          return (
            <a
              key={part.key}
              className={sourceStyles.citationChipLink}
              href={sourceLink}
              onClick={
                opensFilePreview
                  ? (event) => {
                      event.preventDefault();
                      openVersionFilePreview(router, objectId);
                    }
                  : undefined
              }
            >
              {sourceChip}
            </a>
          );
        }

        return (
          <span key={part.key} className={sourceStyles.citationChipWrap}>
            {sourceChip}
          </span>
        );
      })}
    </div>
  );
};
